import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid as Grid2,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  useTheme,
} from "@mui/material";
import Breadcrumb from "../../../../Components/BreadCrumb";
import RefreshButton from "../../../../Components/RefreshButton";
import dayjs, { Dayjs } from "dayjs";
import { styled } from "@mui/material/styles";
import { FaRegFileExcel, FaFilter } from "react-icons/fa6";
import {
  useExportReportMutation,
  useGetPaymentStatusMutation,
} from "../../../../Services/ReportsService";
import {
  PaymentDatum,
  PaymentReportsErrorModel,
} from "../../../../Helpers/Interfaces/PaymentReports";
import ITopupDateRangePicker from "../../../../Components/ITopupDateRangePicker";
import Utils from "../../../../Helpers/Utils";
import { useDispatch, useSelector } from "react-redux";
import PaymentReportsSlidingFilter, {
  PaymentFilters,
} from "../../../../Components/SlidingFilter/PaymentReportsSlidingFilter";
import AppliedFilter from "../../../../Components/AppliedFilter";
import ExportButton from "../../../../Components/ExportButton";
import { useProductDenominationLabels } from "../../../../Components/ProductDenominationFilter";
import ApiPath from "../../../../Services/ConstantApi";
import downloadReport, {
  datedFileName,
} from "../../../../Helpers/Export/downloadReport";
import { showSnackbar } from "../../../../Redux/Slices/NotificationSlice";

interface Filters {
  dateRange: [Dayjs | null, Dayjs | null];
  paymentStatus: string;
  paymentMethod: string;
  customerName: string;
  search: string;
}

const FilterGrid = styled(Grid2)`
  &.filter-grid {
    .MuiInputBase-root {
      height: 35px;
      min-height: 35px;

      .MuiOutlinedInput-input {
        padding: 5px 14px;
      }

      .MuiSelect-select {
        padding: 5px 14px;
        font-size: 12px;
      }

      .MuiInputLabel-root {
        transform: translate(14px, 8px) scale(1);
        &.MuiInputLabel-shrink {
          transform: translate(14px, -9px) scale(0.75);
        }
      }
    }

    .MuiFormLabel-root {
      top: -3px;
    }
  }
`;

// Type guard to check if the response is an error
const isErrorResponse = (data: any): data is PaymentReportsErrorModel => {
  return (
    data &&
    data.status === "error" &&
    typeof data.statusCode === "number" &&
    typeof data.message === "string"
  );
};

const PaymentReports: React.FC = () => {
  const today = dayjs();
  const theme = useTheme();

  // Reload without losing the filters, page and sort already set up.
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<any>(null);
  const handleRefresh = () => {
    setRefreshing(true);
    setRefreshKey((k) => k + 1);
  };
  const [filters, setFilters] = React.useState<Filters>({
    dateRange: [today, today],
    paymentStatus: "All",
    customerName: "",
    paymentMethod: "all",
    search: "",
  });
  const [filterOpen, setFilterOpen] = useState(false);
  const todayStr = today.format("YYYY-MM-DD");

  // Sliding filter local state and applied combined state
  const [paymentFilters, setPaymentFilters] = useState<PaymentFilters>({
    startDate: todayStr,
    endDate: todayStr,
    paymentStatus: "All",
    orderNumber: "",
    customerName: "",
  });

  interface CombinedState {
    page: number;
    pageSize: number;
    startDate?: string;
    endDate?: string;
    paymentStatus: string;
    orderNumber: string;
    customerName?: string;
    /** Chosen from the Product dropdown. */
    productId?: string;
    /** The SKU of a denomination of that product. Depends on productId. */
    productDenominationsku?: string;
  }

  const [combinedState, setCombinedState] = useState<CombinedState>({
    page: 0,
    pageSize: 10,
    startDate: todayStr,
    endDate: todayStr,
    paymentStatus: "All",
    orderNumber: "",
    customerName: "",
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [selectedOrder, setSelectedOrder] = React.useState<PaymentDatum | null>(
    null
  );
  const [dialogType, setDialogType] = React.useState<
    "products" | "invoice" | null
  >(null);
  const useInfo = useSelector((state: any) => state.AuthSlice.adminAuthUser);
  const dispatch = useDispatch();

  const [getPaymentListTrigger, { data, isLoading, error }] =
    useGetPaymentStatusMutation();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [exportReport] = useExportReportMutation();
  const [exporting, setExporting] = useState(false);

  /*
   * The filters the screen currently has, in the shape the API takes.
   * Shared by the list below and the export, so the file and the table can
   * only ever be the same set of rows.
   */
  const buildFilters = useCallback(() => {
    const filters: { key: string; value: any }[] = [];

    if (combinedState.orderNumber) {
      filters.push({ key: "orderNumber", value: combinedState.orderNumber });
    }
    if (combinedState.customerName) {
      filters.push({ key: "customer", value: combinedState.customerName });
    }
    if (combinedState.startDate) {
      filters.push({ key: "startDate", value: combinedState.startDate });
    }
    if (combinedState.endDate) {
      filters.push({ key: "endDate", value: combinedState.endDate });
    }
    if (combinedState.paymentStatus && combinedState.paymentStatus !== "All") {
      filters.push({
        key: "paymentStatus",
        value: combinedState.paymentStatus,
      });
    }

    // Product and denomination, chosen from the dropdowns.
    if (combinedState.productId) {
      filters.push({ key: "productId", value: combinedState.productId });
    }
    if (combinedState.productDenominationsku) {
      filters.push({
        key: "productDenominationsku",
        value: combinedState.productDenominationsku,
      });
    }

    return filters;
  }, [
    combinedState.orderNumber,
    combinedState.customerName,
    combinedState.startDate,
    combinedState.endDate,
    combinedState.paymentStatus,
    combinedState.productId,
    combinedState.productDenominationsku,
  ]);

  useEffect(() => {
    const filterReq: any = {
      filters: buildFilters(),
      page: page + 1,
      pageSize: rowsPerPage,
    };

    getPaymentListTrigger(filterReq).finally(() => {
      setRefreshing(false);
      setLastUpdated(dayjs());
    });
  }, [
    refreshKey,
    page,
    rowsPerPage,
    combinedState.orderNumber,
    combinedState.customerName,
    combinedState.startDate,
    combinedState.endDate,
    combinedState.paymentStatus,
    combinedState.productId,
    combinedState.productDenominationsku,
  ]);

  /*
   * Export what is on screen.
   *
   * Same filters, same endpoint, same query on the server, without
   * pagination. See Helpers/Export/downloadReport.
   *
   * What this replaces: GET /payments/export, which was the only export of
   * the five that would actually have produced a file -- and had no button
   * to press. It had also drifted from the screen: it matched the status
   * filter on `paymenStatus`, a misspelling, and wrote the value into
   * `productType`, so choosing a status quietly did nothing; and its four
   * columns had neither the customer, the payment type nor the reference id.
   */
  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await exportReport({
        url: ApiPath.paymentList,
        body: { filters: buildFilters() },
      }).unwrap();

      const result = await downloadReport(blob, datedFileName("payment-report"));
      if (!result.ok) {
        dispatch(showSnackbar({ message: result.message, severity: "warning" }));
      }
    } catch (err: any) {
      dispatch(
        showSnackbar({
          message: err?.data?.message || "The export could not be produced.",
          severity: "error",
        }),
      );
    } finally {
      setExporting(false);
    }
  };

  const handleSearchInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setPage(0); // Reset to first page on new search
    }, 300);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    // console.log(data, "data-data-data");
  }, [data]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field: keyof Filters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleReset = () => {
    setFilters({
      ...filters,
      paymentStatus: "",
      dateRange: [today, today],
    });
    setSearchTerm("");
    setPage(0);
    setRowsPerPage(10);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "paid":
        return "success.main";
      case "pending":
        return "warning.main";
      case "failed":
        return "error.main";
      default:
        return "error.main";
    }
  };

  const handleCloseDialog = () => {
    setSelectedOrder(null);
    setDialogType(null);
  };

  const isDateChanged = Utils.isDateChanged(filters.dateRange);

  // Sliding filter handlers
  const handleSlidingFilterChange = (
    name: keyof PaymentFilters,
    value: any
  ) => {
    setPaymentFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = (filtersToApply: PaymentFilters) => {
    setPaymentFilters(filtersToApply);
    setCombinedState((prev) => ({
      ...prev,
      page: 0,
      startDate: filtersToApply.startDate || "",
      endDate: filtersToApply.endDate || "",
      paymentStatus: filtersToApply.paymentStatus || "All",
      orderNumber: filtersToApply.orderNumber || "",
      customerName: (filtersToApply as any).customerName || "",
      productId: filtersToApply.productId || "",
      // A denomination only means anything alongside its product. Without one
      // it filters on a SKU nothing on the panel still shows.
      productDenominationsku: filtersToApply.productId
        ? filtersToApply.productDenominationsku || ""
        : "",
    }));
    setFilterOpen(false);
  };

  const handleClearFilters = () => {
    setPaymentFilters({
      startDate: "",
      endDate: "",
      paymentStatus: "All",
      orderNumber: "",
      customerName: "",
      productId: "",
      productDenominationsku: "",
    });
    setCombinedState((prev) => ({
      ...prev,
      page: 0,
      startDate: "",
      endDate: "",
      paymentStatus: "All",
      orderNumber: "",
      customerName: "",
      productId: "",
      productDenominationsku: "",
    }));
  };

  const handleClearSingleFilter = (filterKey: string) => {
    const updates: Partial<CombinedState> = { page: 0 };

    if (filterKey === "paymentStatus") {
      updates.paymentStatus = "All";
      setPaymentFilters((prev) => ({ ...prev, paymentStatus: "All" }));
    } else if (filterKey === "orderNumber") {
      updates.orderNumber = "";
      setPaymentFilters((prev) => ({ ...prev, orderNumber: "" }));
    } else if (filterKey === "customerName") {
      updates.customerName = "";
      setPaymentFilters((prev) => ({ ...prev, customerName: "" }));
    } else if (filterKey === "startDate") {
      (updates as any).startDate = "";
      setPaymentFilters((prev) => ({ ...prev, startDate: "" }));
    } else if (filterKey === "endDate") {
      (updates as any).endDate = "";
      setPaymentFilters((prev) => ({ ...prev, endDate: "" }));
    }

    /*
     * Clearing the product clears the denomination with it. A denomination
     * on its own filters on a SKU with no product beside it -- invisible on
     * the panel, and matching nothing.
     */
    if (filterKey === "productId") {
      (updates as any).productId = "";
      (updates as any).productDenominationsku = "";
      setPaymentFilters((prev) => ({
        ...prev,
        productId: "",
        productDenominationsku: "",
      }));
    }
    if (filterKey === "productDenominationsku") {
      (updates as any).productDenominationsku = "";
      setPaymentFilters((prev) => ({ ...prev, productDenominationsku: "" }));
    }

    // Update combined state for API calls
    setCombinedState((prev) => ({ ...prev, ...updates }));
  };

  // Names for the Product and Denomination chips. Same cached query the
  // dropdowns use, so it costs no request.
  const { productLabel, denominationLabel } = useProductDenominationLabels(
    combinedState.productId,
    combinedState.productDenominationsku,
  );

  const appliedFiltersForChips = {
    paymentStatus:
      combinedState.paymentStatus === "All" ? "" : combinedState.paymentStatus,
    orderNumber: combinedState.orderNumber || "",
    customerName: combinedState.customerName || "",
    startDate: (combinedState as any).startDate || "",
    endDate: (combinedState as any).endDate || "",
    productId: productLabel,
    productDenominationsku: denominationLabel,
  };

  return (
    <Box
      sx={{
        p: 0,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Breadcrumb
          name="Payment Reports"
          items={[{ label: "Reports" }, { label: "Payment Reports" }]}
        />
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
          <AppliedFilter
            filters={appliedFiltersForChips}
            onClearFilter={handleClearSingleFilter}
            onClearAll={handleClearFilters}
            filterLabels={{
              productId: "Product",
              productDenominationsku: "Denomination",
              paymentStatus: "Payment Status",
              orderNumber: "Order / Payment / Reference",
              customerName: "Customer",
              startDate: "Start Date",
              endDate: "End Date",
            }}
            customFormatters={{
              startDate: (v: string) =>
                v ? dayjs(v).format("YYYY-MM-DD") : "",
              endDate: (v: string) =>
                v ? dayjs(v).format("YYYY-MM-DD") : "",
              paymentStatus: (v: string) =>
                v
                  ? v
                      .split("_")
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1)
                      )
                      .join(" ")
                  : "",
            }}
          />
          <RefreshButton onRefresh={handleRefresh} refreshing={refreshing} lastUpdated={lastUpdated} />
          <ExportButton onExport={handleExport} exporting={exporting} />
          <Button
            color="secondary"
            size={"medium"}
            sx={{
              height: 34,
              textTransform: "capitalize",
              boxShadow: theme.shadows[1],
            }}
            variant="outlined"
            startIcon={<FaFilter size={16} />}
            onClick={() => setFilterOpen(true)}
          >
            &nbsp;<span>Apply Filters</span>
          </Button>
        </Box>
      </Box>

      <Box
        sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}
      >
        {/* Payment Reports Table */}
        <TableContainer component={Paper}>
          <Table
            stickyHeader
            sx={{
              "& .MuiTableCell-root": {
                padding: "8px 12px",
                fontSize: "14px",
                borderBottom: "1px solid #e0e0e0",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
              "& .MuiTableHead-root .MuiTableCell-root": {
                backgroundColor: "#f9fafb",
                fontWeight: "bold",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 70 }}>#</TableCell>
                <TableCell sx={{ width: 150 }}>Order Number</TableCell>
                <TableCell sx={{ width: 200 }}>Customer</TableCell>
                <TableCell sx={{ width: 160 }}>Date</TableCell>
                <TableCell sx={{ width: 160 }}>Payment Type</TableCell>
                <TableCell sx={{ width: 200 }}>Payment / Reference ID</TableCell>
                <TableCell sx={{ width: 140 }}>Status</TableCell>
                <TableCell sx={{ width: 140, textAlign: "right" }}>
                  Total Amount
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading &&
                [...Array(10)].map((_, index) => (
                  <TableRow key={index}>
                    {[...Array(8)].map((_, colIndex) => (
                      <TableCell key={colIndex}>
                        <Skeleton
                          variant="text"
                          width={colIndex === 0 ? 30 : "80%"}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

              {!isLoading &&
                data &&
                !isErrorResponse(data) &&
                data.data &&
                data.data.map((report, index: number) => (
                  <TableRow key={report._id}>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>

                    <TableCell>{report.orderNumber}</TableCell>

                    <TableCell>
                      {report.userId?.firstName
                        ? `${report.userId.firstName} ${report.userId.lastName}`
                        : report.userId?.lastName || "N/A"}
                      <br />
                      <small style={{ color: "gray", fontSize: "12px" }}>
                        {report?.userId?.email || "N/A"}
                      </small>
                    </TableCell>

                    <TableCell>
                      {dayjs(report.createdAt).format("DD.MM.YYYY, HH.mm.ss")}
                    </TableCell>

                    <TableCell sx={{ textTransform: "capitalize" }}>
                      {report?.paymentSource || "N/A"}
                    </TableCell>

                    {/* A bank transfer has no paymentId (empty string), so this
                        column read "N/A" for every one of them. Its identifier is
                        the order's referenceNumber, BANKTRANSFER-nnnn. The two are
                        mutually exclusive -- a payment is online or bank transfer,
                        never both -- so one column carries both. Bank transfers
                        before Apr 2026 predate the reference and stay "N/A". */}
                    <TableCell>
                      {report?.paymentId || report?.referenceNumber || "N/A"}
                    </TableCell>

                    <TableCell>
                      <Typography color={getStatusColor(report.paymentStatus)}>
                        {report.paymentStatus.charAt(0).toUpperCase() +
                          report.paymentStatus.slice(1)}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ textAlign: "right" }}>
                      {Utils.formatEuro((report.orderTotal || 0).toFixed(2))}
                    </TableCell>
                  </TableRow>
                ))}

              {!isLoading && error && (error as any)?.data?.message && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    {(error as any).data.message}
                  </TableCell>
                </TableRow>
              )}

              {!isLoading &&
                data &&
                isErrorResponse(data) &&
                data.statusCode === 200 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      {data.message}
                    </TableCell>
                  </TableRow>
                )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Box sx={{ background: theme.palette.background.default }}>
        <TablePagination
          showFirstButton
          showLastButton
          component="div"
          count={
            data && !isErrorResponse(data) && data.totalRecords
              ? data.totalRecords
              : 0
          }
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Box>

      <PaymentReportsSlidingFilter
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        filters={paymentFilters}
        onFilterChange={handleSlidingFilterChange}
      />

      <Dialog
        open={!!selectedOrder}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {dialogType === "products" ? "Order Products" : "Invoice Details"} -{" "}
          {selectedOrder?.orderNumber}
        </DialogTitle>
        <DialogContent dividers>
          {dialogType === "products" ? (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell align="center">Quantity</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody></TableBody>
            </Table>
          ) : (
            <Box>
              {/* Add your invoice view component here */}
              <Typography>Invoice details coming soon...</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentReports;
